import { useEffect } from "react";
import { HashLink } from "react-router-hash-link";
import GoogleAnalytics from "react-ga4";
import "../components/pages/features/features.css";
import Feature from "../components/pages/features/Feature";
import inventory from "../assets/img/features/optimized/equipment.webp";
import projectManagement from "../assets/img/features/optimized/jobs.webp";
import stock from "../assets/img/features/optimized/stock.webp";
import maintenance from "../assets/img/features/optimized/maintenance.webp";
import reporting from "../assets/img/features/optimized/reporting.webp";
import qrScan from "../assets/img/features/optimized/qr.webp";
import gps from "../assets/img/features/optimized/gps.webp";
import devSupport from "../assets/img/features/optimized/onboarding.webp";
import discussingCustomising from "../assets/img/features/optimized/development.webp";

const features = [
    {
        title: "Equipment management",
        subtitle: "Know what you have and where it is",
        description: <>
            <p>Give each item of equipment a central record. Bring its details, location and history together so your team can find the information they need without searching through separate spreadsheets and files.</p>
            <ul>
                <li>Manage your equipment inventory and individual item records.</li>
                <li>Keep track of component assemblies and the items they contain.</li>
                <li>See equipment locations and movement history.</li>
                <li>Access related maintenance records and documents.</li>
            </ul>
        </>,
        img: inventory,
        imageAlt: "equi-track equipment inventory displayed on a desktop monitor"
    },
    {
        title: "Jobs and quotations",
        subtitle: "From quotation to equipment return",
        description: <>
            <p>Connect each job with the equipment you supply. Keep quotations, equipment allocations and returns together, giving your team a clearer picture of what is going out, where it is being used and what has come back.</p>
            <ul>
                <li>Create quotations.</li>
                <li>Manage jobs.</li>
                <li>Allocate equipment to a job.</li>
                <li>Record equipment returns.</li>
            </ul>
        </>,
        img: projectManagement,
        imageAlt: "Project planning timeline illustrating the organisation of jobs"
    },
    {
        title: "Parts and stock",
        subtitle: "Keep control of stock, from purchase to use",
        description: <>
            <p>Bring purchasing and stock control into the same system as your equipment records. Follow parts from delivery into stock and through to their use in equipment, helping your team keep a clearer record of what they have and where it has gone.</p>
            <ul>
                <li>Manage purchase orders and record deliveries.</li>
                <li>Carry out stock takes.</li>
                <li>Prepare pick lists.</li>
                <li>Trace parts from stock through to use in equipment.</li>
            </ul>
        </>,
        img: stock,
        imageAlt: "Labelled stock boxes illustrating inventory identification and traceability"
    },
    {
        title: "Maintenance and certification",
        subtitle: "Keep maintenance organised and records together",
        description: <>
            <p>Help your team see what needs attention and find the history and paperwork for each item. Keep inspection records, servicing information and certificate documents connected to the equipment they relate to.</p>
            <ul>
                <li>Keep inspection records against equipment.</li>
                <li>Maintain service history.</li>
                <li>Use reminders to help keep maintenance on schedule.</li>
                <li>Store certificate documents with the relevant equipment record.</li>
            </ul>
        </>,
        img: maintenance,
        imageAlt: "Engineer consulting a laptop in an equipment workshop"
    },
    {
        title: "Reporting",
        subtitle: "Understand how your equipment is performing",
        description: <>
            <p>Turn the information recorded during everyday work into a clearer view of equipment performance. Understand how equipment is being used and how it contributes to your jobs, helping you plan work and make informed investment decisions.</p>
            <ul>
                <li>Review equipment utilisation.</li>
                <li>Understand job costs.</li>
                <li>See income by item.</li>
            </ul>
        </>,
        img: reporting,
        imageAlt: "Illustration of charts and data analysis alongside a laptop"
    },
    {
        title: "QR codes",
        subtitle: "Scan straight to the equipment record",
        description: <>
            <p>Identify an item and open its record without searching or typing its details. QR labels give your team a quick route from the equipment in front of them to the information held in equi-track.</p>
            <ul>
                <li>Create and print your own QR labels.</li>
                <li>Scan a label to identify equipment.</li>
                <li>Open the corresponding equipment record.</li>
            </ul>
        </>,
        img: qrScan,
        imageAlt: "Phone scanning a QR code"
    },
    {
        title: "GPS tracking and monitoring",
        subtitle: "Additional visibility when you need it",
        description: <>
            <p>Where your operation needs tracking or sensor information, compatible GPS trackers and IoT sensors can add another layer of visibility to your equipment management.</p>
            <p>View recorded locations and movement history on a map. We work with you to discuss suitable devices and the monitoring requirements for your equipment.</p>
        </>,
        img: gps,
        imageAlt: "equi-track location map displayed on a mobile phone",
        link: "/#contact",
        btnText: "Discuss your tracking requirements"
    },
    {
        title: "Onboarding and support",
        subtitle: "We help you get it working for your business",
        description: <>
            <p>Moving to a central system is an important step for your business. We work alongside you to understand your processes, prepare your setup and data, and help your team get started with confidence.</p>
            <ul>
                <li>Understand how your business works and what you need.</li>
                <li>Help prepare your setup and migrate existing data.</li>
                <li>Train your users and support adoption.</li>
                <li>Provide ongoing support as your team uses equi-track.</li>
            </ul>
            <p>We can also discuss integration with existing systems and database options to suit your requirements.</p>
        </>,
        img: devSupport,
        imageAlt: "Support specialist speaking with a customer online",
        link: "/#contact",
        btnText: "Talk to us about getting started"
    },
    {
        title: "Ongoing development",
        subtitle: "Developed by us. Shaped with our customers.",
        description: <>
            <p>We develop equi-track ourselves. Our in-house development team is continually focused on innovation and practical improvements to the way businesses manage their equipment.</p>
            <p>When you become a customer, you become part of that development process. Your team can bring ideas and everyday challenges directly into our feedback loop. Every idea is listened to and considered as we shape future improvements.</p>
            <p>If you have a particular process or customisation requirement, we can explore it with you.</p>
        </>,
        img: discussingCustomising,
        imageAlt: "Team discussing ideas and requirements together",
        link: "/#contact",
        btnText: "Discuss your requirements"
    }
];

export default function Features() {
    useEffect(() => {
        GoogleAnalytics.send({ hitType: "pageview", page: window.location.pathname, title: "Features" });
    }, []);

    return (
        <main className="below-navbar d4-container features-page">
            <div className="features-container">
                <header className="features-section">
                    <h1 className="text-center">Features</h1>
                    <div className="features-subtitle-text text-center">
                        <p className="lead">One platform for equipment, jobs, stock and maintenance.</p>
                        <p>Bring your records and daily processes together, so your team spends less time chasing information and more time getting work done.</p>
                        <HashLink smooth to="/#contact" className="btn btn-primary btn-lg mt-2 mb-4">Book a demonstration</HashLink>
                    </div>
                </header>
                <div>
                    {features.map((feature, index) => (
                        <Feature feature={feature} index={index} key={feature.title} />
                    ))}
                </div>
                <section className="features-demo text-center" aria-labelledby="features-demo-heading">
                    <h2 id="features-demo-heading">See how equi-track could work for your business.</h2>
                    <p>Tell us what you manage and what you want to improve. We’ll arrange a demonstration through TEAMS, or discuss an in-person meeting if you prefer.</p>
                    <HashLink smooth to="/#contact" className="btn btn-primary btn-lg">Book a demonstration</HashLink>
                </section>
            </div>
        </main>
    );
}
